<template>
  <div id="app">
    <navbar />

    <b-container v-if="loading" class="loading">
      <img src="/lowerisk.gif" />
    </b-container>

    <b-container v-else>
      <Welcome v-if="$auth.loading || !$auth.isAuthenticated" />
      <div v-else>
        <Breadcrumbs :current-screen="currentScreen" />

        <b-form-input
          v-if="currentScreen != 'enterKey'"
          v-model="filter"
          placeholder="filter display"
        >
        </b-form-input>

        <div v-if="currentScreen === 'services'" class="d-flex align-items-last-baseline">
          <b-form-group label="Show only services with:">
            <b-form-checkbox-group
              id="featureFilters"
              v-model="featureFilter"
              :options="featureFilterOptions"
              name="featureFilters"
            ></b-form-checkbox-group>
          </b-form-group>
          <b-spinner
            v-if="featureFilterRunning"
            variant="success"
            label="spinning"
            class="m-1"
          ></b-spinner>
        </div>

        <!-- Multi-purpose component for service list -->
        <component
          :is="currentScreen"
          v-if="currentScreen != 'tables'"
          :items="activeData"
          :title="activeTitle"
          @screen="updateScreen"
          @apikey="updateApiKey"
          @active="updateActive"
          @patch="doPatch"
        ></component>

        <!-- Display for the acl/dictionary portion -->
        <div v-else>
          <acls :items="activeAcls" @screen="updateScreen" @active="updateActive" />
          <dictionaries :items="activeDictionaries" @screen="updateScreen" @active="updateActive" />
        </div>
      </div>
    </b-container>
  </div>
</template>

<script>
import _ from 'lodash';
import acls from './components/acls.vue';
import dictionaries from './components/dictionaries.vue';
import enterKey from './components/enterKey.vue';
import services from './components/services.vue';
import aclTable from './components/aclTable.vue';
import dictionaryTable from './components/dictionaryTable.vue';
import navbar from './components/navbar.vue';
import Breadcrumbs from './components/breadcrumbs.vue';
import Welcome from './components/welcome.vue';

import filters from './filters';

export default {
  name: 'App',
  components: {
    acls,
    dictionaries,
    enterKey,
    services,
    aclTable,
    dictionaryTable,
    navbar,
    Breadcrumbs,
    Welcome,
  },
  data() {
    return {
      filter: '',
      featureFilterRunning: false,
      featureFilter: [],
      featureFilterOptions: [
        { text: 'Image Optimization', value: 'io' },
        { text: 'WAF', value: 'waf' },
        { text: 'Blocked Traffic Log', value: 'block_logs' },
      ],
      loading: false,
      currentScreen: 'enterKey',
      active: {
        service: false,
        acl: false,
        dictionary: false,
        title: false,
      },
      rawServices: {},
      rawAcls: {},
      rawDictionaries: {},
      rawAclEntries: {},
      rawDictionaryEntries: {},
    };
  },
  computed: {
    activeTitle() {
      const vm = this;
      switch (vm.currentScreen) {
        case 'dictionaryTable':
          return vm.activeDictionary.name;
        case 'aclTable':
          return vm.activeAcl.name;
        default:
          return '';
      }
    },
    activeData() {
      const vm = this;
      if (!vm.loading) {
        if (vm.currentScreen === 'services') {
          let toReturn = vm.activeServices;
          if (vm.featureFilter.includes('io')) {
            vm.featureFilterRunning = true;
            toReturn = toReturn.filter(
              d =>
                'clients' in d &&
                d.clients.length > 0 &&
                'settings' in d.clients[0] &&
                'hasIo' in d.clients[0].settings
            );
            vm.featureFilterRunning = false;
          }
          if (vm.featureFilter.includes('waf')) {
            vm.featureFilterRunning = true;
            toReturn = toReturn.filter(
              d =>
                'clients' in d &&
                d.clients.length > 0 &&
                'settings' in d.clients[0] &&
                'hosts' in d.clients[0].settings &&
                d.clients[0].settings.hosts.filter(domain =>
                  /\.waf\.ps-pantheon\.com$/.test(domain)
                ).length > 0
            );
            vm.featureFilterRunning = false;
          }
          if (vm.featureFilter.includes('block_logs')) {
            vm.featureFilterRunning = true;
            toReturn = toReturn.filter(
              d =>
                'clients' in d &&
                d.clients.length > 0 &&
                'settings' in d.clients[0] &&
                'hasBlockLog' in d.clients[0].settings
            );
            vm.featureFilterRunning = false;
          }
          return toReturn;
        }
        if (vm.currentScreen === 'dictionaryTable' && vm.active.service && vm.active.dictionary) {
          return filters.dictionaryEntries(
            vm.rawDictionaryEntries[vm.active.service][vm.active.dictionary],
            vm.filter
          );
        }
        if (vm.currentScreen === 'aclTable' && vm.active.service && vm.active.acl) {
          return filters.aclEntries(vm.rawAclEntries[vm.active.service][vm.active.acl], vm.filter);
        }
      }
      return {};
    },
    activeServices() {
      const vm = this;
      if (vm.filter === '') return vm.rawServices || [];
      return filters.service(vm.rawServices, vm.filter);
    },
    activeService() {
      const vm = this;
      if (vm.active.service) {
        return _.find(vm.activeServices, service => {
          return service.id === vm.active.service;
        });
      }
      return {};
    },
    activeAcls() {
      const vm = this;
      if (!vm.loading && vm.active.service) {
        return filters.acl(vm.rawAcls[vm.active.service], vm.filter);
      }
      return [];
    },
    activeAcl() {
      const vm = this;
      if (!vm.loading && vm.active.acl) {
        return _.find(vm.activeAcls, { id: vm.active.acl });
      }
      return {};
    },
    activeDictionaries() {
      const vm = this;
      if (!vm.loading && vm.active.service) {
        return filters.dictionary(vm.rawDictionaries[vm.active.service], vm.filter);
      }
      return [];
    },
    activeDictionary() {
      const vm = this;
      if (!vm.loading && vm.active.dictionary) {
        return _.find(vm.activeDictionaries, { id: vm.active.dictionary });
      }
      return {};
    },
  },
  methods: {
    updateScreen(newScreen) {
      const vm = this;
      vm.currentScreen = newScreen;
      vm.filter = '';
    },
    async updateApiKey(newKey) {
      const vm = this;
      vm.$api.init(newKey);
      if (vm.$api._ready) {
        vm.loading = true;
        const result = await vm.$api.fetch('/services');
        // @TODO validate response
        vm.rawServices = result.data;
        vm.loading = false;
      } else {
        // should be impossible, but we know how that goes.
        // eslint-disable-next-line no-console
        console.error('api not ready!');
      }
    },
    updateActive(params) {
      const vm = this;
      _.each(params, (param, key) => {
        vm.active[key] = param;
        vm.load(key);
      });
      // @TODO set active service, acl or dictionary for other screens reference.
    },
    async load(type) {
      const vm = this;
      vm.loading = true;
      if (type === 'service') {
        // grab child acls and dictionaries
        const serviceAclsResponse = await vm.$api.fetch(
          `/service/${vm.active.service}/version/${vm.activeService.version}/acls`
        );
        // Don't allow editing of the waf allowlist by either of its names
        vm.rawAcls[vm.active.service] = serviceAclsResponse.data.filter(
          acl => !acl.name.match(/^waf (allow|white)list$/)
        );
        const serviceDictionariesResponse = await vm.$api.fetch(
          `/service/${vm.active.service}/version/${vm.activeService.version}/dictionaries`
        );
        vm.rawDictionaries[vm.active.service] = serviceDictionariesResponse.data;
      }
      if (type === 'acl') {
        // fetch acl entries

        const aclEntriesResponse = await vm.$api.fetch(
          `/service/${vm.active.service}/version/${vm.activeService.version}/acl/${vm.active.acl}`
        );
        _.set(vm.rawAclEntries, [vm.active.service, vm.active.acl], aclEntriesResponse.data);
      }
      if (type === 'dictionary') {
        // fetch dictionary entries
        const dictionaryEntriesResponse = await vm.$api.fetch(
          `/service/${vm.active.service}/version/${vm.activeService.version}/dictionary/${vm.active.dictionary}`
        );
        _.set(
          vm.rawDictionaryEntries,
          [vm.active.service, vm.active.dictionary],
          dictionaryEntriesResponse.data
        );
      }
      vm.loading = false;
    },
    async doPatch(params) {
      const vm = this;
      const type = _.first(_.keys(params));
      let response;
      switch (type) {
        case 'acl':
          vm.loading = true;
          response = await vm.$api.patch(
            `/service/${vm.active.service}/version/${vm.activeService.version}/acl/${vm.active.acl}`,
            {
              entries: params[type],
            }
          );
          // eslint-disable-next-line no-console
          console.log({ response });
          // @TODO reload rawEntries
          await vm.load(type);
          vm.loading = false;
          break;
        case 'dictionary':
          vm.loading = true;
          response = await vm.$api.patch(
            `/service/${vm.active.service}/version/${vm.activeService.version}/dictionary/${vm.active.dictionary}`,
            {
              items: params[type],
            }
          );
          // eslint-disable-next-line no-console
          console.log({ response });
          await vm.load(type);
          vm.loading = false;
          break;

        default:
          // eslint-disable-next-line no-console
          console.error('unknown patch param');
          break;
      }
    },
  },
};
</script>

<style>
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
}
.loading img {
  height: 100vh;
  left: 50%;
}
.align-items-last-baseline {
  align-items: last baseline !important;
}
</style>
